import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel, Spinner, StatusBadge } from "../components/ui-kit";
import { Plus, X, Trash2, Pencil, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", website: "", account_url: "", referral_link: "", referral_code: "", platform: "MT5", countries: "", regulation: "", status: "Active", logo: null, id: null };

const resizeLogo = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const max = 128;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = reject; img.src = reader.result;
  };
  reader.onerror = reject; reader.readAsDataURL(file);
});

export default function Brokers() {
  const [brokers, setBrokers] = useState(null);
  const [refs, setRefs] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | broker.id | null
  const [form, setForm] = useState(BLANK);

  const load = () => { client.get("/brokers").then(({ data }) => setBrokers(data)); client.get("/brokers/referrals").then(({ data }) => setRefs(data)); };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(BLANK); setEditing("new"); };
  const openEdit = (b) => {
    setForm({ name: b.name, website: b.website || "", account_url: b.account_url || "", referral_link: b.referral_link || "",
      referral_code: b.referral_code || "", platform: b.platform || "MT5", countries: (b.countries || []).join(", "),
      regulation: b.regulation || "", status: b.status || "Active", logo: b.logo || null, id: b.id });
    setEditing(b.id);
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large — pick one under 2MB"); return; }
    try { const dataUrl = await resizeLogo(file); setForm((f) => ({ ...f, logo: dataUrl })); }
    catch { toast.error("Could not read image"); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Broker name is required"); return; }
    const payload = { ...form, countries: form.countries.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (form.id) await client.patch(`/brokers/${form.id}`, payload);
      else await client.post("/brokers", payload);
      toast.success(form.id ? "Broker updated" : "Broker added"); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Remove broker "${b.name}"? This cannot be undone.`)) return;
    try { await client.delete(`/brokers/${b.id}`); toast.success(`${b.name} removed`); load(); }
    catch { toast.error("Failed to remove"); }
  };

  if (!brokers) return <Layout title="Broker Partners"><Spinner /></Layout>;

  return (
    <Layout title="Broker Partners">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-xs text-slate-500">{brokers.length} partner{brokers.length === 1 ? "" : "s"} · Regulatory info must be manually verified — never auto-claimed.</div>
        <button data-testid="add-broker-btn" onClick={openAdd} className="ml-auto btn-gold px-3 py-1.5 text-xs flex items-center gap-1.5"><Plus size={14} />Add Broker</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
        {brokers.length === 0 && <div className="surface p-8 text-center text-slate-500 text-sm col-span-full">No broker partners yet. Click "Add Broker" to add your first partner.</div>}
        {brokers.map((b) => (
          <div key={b.id} className="surface p-4" data-testid={`broker-${b.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {b.logo
                  ? <img src={b.logo} alt={b.name} className="w-9 h-9 object-contain bg-white/5 rounded" />
                  : <div className="w-9 h-9 flex items-center justify-center bg-gold text-black font-head font-bold">{b.name[0]}</div>}
                <div><div className="font-head font-semibold text-white">{b.name}</div><div className="text-xs text-slate-500">{b.platform}</div></div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={b.status} />
                <button data-testid={`edit-broker-${b.id}`} onClick={() => openEdit(b)} title="Edit broker" className="text-slate-500 hover:text-gold transition-colors duration-200"><Pencil size={14} /></button>
                <button data-testid={`delete-broker-${b.id}`} onClick={() => remove(b)} title="Remove broker" className="text-slate-500 hover:text-bear transition-colors duration-200"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 font-mono text-center">
              <div><div className="text-lg text-white">{b.clicks}</div><div className="text-[10px] text-slate-500 uppercase">Clicks</div></div>
              <div><div className="text-lg text-gold">{b.applications}</div><div className="text-[10px] text-slate-500 uppercase">Apps</div></div>
              <div><div className="text-lg text-bull">{b.verified}</div><div className="text-[10px] text-slate-500 uppercase">Verified</div></div>
            </div>
            <div className="mt-3 text-xs text-slate-500">{(b.countries || []).join(", ")}</div>
            <div className="mt-2 text-xs text-slate-600 flex items-center gap-1"><span className="font-mono">/go/{b.slug}</span></div>
            <div className="mt-1 text-[11px] text-slate-500 italic">{b.regulation}</div>
          </div>
        ))}
      </div>

      <Panel title="Referral Tracking" right={<span className="text-xs text-slate-500">{refs.length} events</span>}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[color:var(--border-subtle)]">
              <th className="px-4 py-2.5">Broker</th><th className="px-4 py-2.5">Source</th><th className="px-4 py-2.5">Campaign</th><th className="px-4 py-2.5">Country</th><th className="px-4 py-2.5">Status</th>
            </tr></thead>
            <tbody>
              {refs.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500 text-sm">No referral activity yet.</td></tr>}
              {refs.slice(0, 30).map((r) => (
                <tr key={r.id} className="border-b border-[color:var(--border-subtle)] hover:bg-white/5 transition-colors duration-150">
                  <td className="px-4 py-2.5 text-slate-200">{r.broker}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{r.utm_source}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-400">{r.campaign || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-400">{r.country || "—"}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative w-full max-w-md surface p-5 max-h-[90vh] overflow-y-auto" data-testid="broker-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-lg text-white">{form.id ? "Edit Broker" : "Add Broker Partner"}</h3>
              <button type="button" onClick={() => setEditing(null)}><X size={18} className="text-slate-400" /></button>
            </div>

            <div className="flex items-center gap-3 mb-4">
              {form.logo
                ? <img src={form.logo} alt="logo" className="w-14 h-14 object-contain bg-white/5 rounded border border-[color:var(--border-subtle)]" />
                : <div className="w-14 h-14 flex items-center justify-center bg-white/5 rounded border border-dashed border-[color:var(--border-subtle)] text-slate-600 text-xs">Logo</div>}
              <div className="flex flex-col gap-1.5">
                <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200">
                  <ImagePlus size={13} />Upload logo
                  <input data-testid="broker-logo-input" type="file" accept="image/*" onChange={onLogo} className="hidden" />
                </label>
                {form.logo && <button type="button" onClick={() => setForm({ ...form, logo: null })} className="text-[11px] text-slate-500 hover:text-bear transition-colors">Remove logo</button>}
              </div>
            </div>

            {[["name", "Broker name"], ["website", "Website"], ["account_url", "Account-opening URL"], ["referral_link", "Referral / IB link"], ["referral_code", "Referral code"], ["countries", "Countries (comma separated, or 'All')"], ["regulation", "Regulatory info (manual)"]].map(([k, ph]) => (
              <input key={k} required={k === "name"} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`broker-input-${k}`}
                className="w-full mb-3 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
            ))}

            <div className="flex items-center gap-3 mb-4">
              <input placeholder="Platform (e.g. MT5)" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="broker-input-platform"
                className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
              <select data-testid="broker-input-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
                <option value="Active">Active</option>
                <option value="Paused">Paused</option>
              </select>
            </div>

            <button data-testid="submit-broker" className="btn-gold w-full py-2 text-sm">{form.id ? "Save Changes" : "Add Broker"}</button>
          </form>
        </div>
      )}
    </Layout>
  );
}
